import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import {
  GetSourcingStrategyApprovalResponse,
  SourcingStrategyApprovalViewProps,
} from '@dreamai/types';
import ProspectsToApprove from './ProspectsToApprove';
import ErrorState from '../../../common/ErrorState';
import './sourcing-strategy-approval.scss';

interface SourcingStrategyApprovalViewComponentProps
  extends SourcingStrategyApprovalViewProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const SourcingStrategyApprovalView: React.FC<
  SourcingStrategyApprovalViewComponentProps
> = ({ sourcingStrategyId, threadId, updateThreadViewVisualDescription }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<GetSourcingStrategyApprovalResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetSourcingStrategyApprovalResponse>(
          `/campaigns/sourcing-strategy/${sourcingStrategyId}/approval-info`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setData(response.data);
        } else {
          setError('Failed to load sourcing strategy information');
        }
      } catch (err: any) {
        console.error('Error fetching sourcing strategy approval info:', err);
        setError(
          err.response?.data?.error || 'Failed to load sourcing strategy',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sourcingStrategyId, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='sourcing-strategy-approval-view'>
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>Loading sourcing strategy...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='sourcing-strategy-approval-view'>
        <ErrorState
          title={error || 'Failed to load sourcing strategy'}
          subtitle='Please try again later.'
        />
      </div>
    );
  }

  return (
    <div className='sourcing-strategy-approval-view'>
      <div className='sourcing-strategy-section'>
        <h3>Strategy for "{data.sourcingStrategy.campaignName}"</h3>
        {data.sourcingStrategy.summary && (
          <div className='strategy-summary'>
            <p>{data.sourcingStrategy.summary}</p>
          </div>
        )}
      </div>

      {data.unapprovedProspectCount > 0 && (
        <div className='prospects-section'>
          <h4>Prospects found with this strategy:</h4>
          <ProspectsToApprove
            campaignId={data.sourcingStrategy.campaignId.toString()}
          />
        </div>
      )}
    </div>
  );
};

export default SourcingStrategyApprovalView;
